<terra-loader *ngIf="isLoading$ | async else content" class="loader"></terra-loader>

<ng-template #content>
  <section class="page-heading">
    <h1>{{(isEditMode$ | async) ? 'Edit a Payment' : 'Send a Payment'}}</h1>
    <h2 class="page-sub-header">Please fill out the form with the relevant information.</h2>
  </section>

  <form class="terra-error-style" [formGroup]="pageForm" (ngSubmit)="submit()">
    <div class="cov-form">
      <!--  Sender Details Section  -->
      <section class="form-section">
        <h3 class="section-title">Sender Details</h3>
        <div class="form-fields">
          <!-- Sender Account -->
          <div class="terra-form-field-wrapper sender-bank-account">
            <label for="senderBankAccount" class="text-input-label">Sender Bank Account</label>
            <div class="sender-bank-account-dropdown">
              <p-dropdown [options]="unitBankAccounts$ | async"
                          styleClass="form-control"
                          [class.p-invalid]="pageForm.get('senderBankAccount').hasError('required')
              && (submitFormError || pageForm.get('senderBankAccount').touched)"
                          formControlName="senderBankAccount"
                          optionValue="id"
                          optionLabel="id"
                          placeholder="Select Bank Account"
                          [disabled]="preSelectedAccountName">
                <ng-template pTemplate="selectedItem">
                  <div class="flex align-items-center gap-2" *ngIf="selectedBankAccount">
                    <terra-option-item
                      [text]="selectedBankAccount.accountNickname"
                      [subText]="subTextOption(selectedBankAccount)"
                      icon="account_balance"></terra-option-item>
                  </div>
                </ng-template>
                <ng-template let-unitBankAccount pTemplate="item">
                  <div class="flex align-items-center gap-2">
                    <terra-option-item
                      [text]="unitBankAccount.accountNickname"
                      [subText]="subTextOption(unitBankAccount)"
                      icon="account_balance"></terra-option-item>
                  </div>
                </ng-template>
              </p-dropdown>
              <mat-error *ngIf="pageForm.get('senderBankAccount').hasError('required')
              && (submitFormError || pageForm.get('senderBankAccount').touched)">
                Required
              </mat-error>
            </div>
          </div>
        </div>
      </section>

      <!--  Recipient Details  -->
      <section class="form-section">
        <h3 class="section-title">Recipient Details</h3>
        <div class="account-source-container">
          <terra-radio-buttons [control]="pageForm.get('accountsSource')" [details]="accountsSource">
          </terra-radio-buttons>
        </div>

        <ng-container *ngIf="pageForm.get('accountsSource').value === AccountsSource.Unit">
          <div class="form-fields">
            <div class="terra-form-field-wrapper">
              <mat-form-field appearance="outline">
                <mat-select id="destinationBankAccount" placeholder="Select From Accounts List" formControlName="destinationBankAccount">
                  <mat-option [value]="null">Select</mat-option>
                  <mat-option *ngFor="let destinationAccount of (unitBankAccountsBesidesSender$ | async)" [value]="destinationAccount.id">
                    {{ destinationAccount.accountNickname }}
                  </mat-option>
                </mat-select>
                <mat-error *ngIf=" pageForm.get('destinationBankAccount').hasError('required')">
                  Required
                </mat-error>
              </mat-form-field>
            </div>

            <div class="terra-form-field-wrapper destination-bank-info" *ngIf="(selectedDestinationAccount | async) as selectedDestinationAccount">
              <ng-container *ngIf="selectedDestinationAccount.parentType === UnitApplicationParentType.Holding">
                <span>
                  Holding Name: <strong>{{ selectedDestinationAccount.holdingName }}</strong>
                </span>
              </ng-container>
              <span>
                ABA Routing Number: <strong>{{ selectedDestinationAccount.routingNumber }}</strong>
              </span>
              <span>
                Account Number: <strong>{{ selectedDestinationAccount.accountNumber | accountNumberToAstrix : 4 }}</strong>
              </span>
            </div>
          </div>
        </ng-container>

        <div class="payment-method-container" *ngIf="pageForm.get('accountsSource').value === AccountsSource.NewExternal 
        || !!(selectedExistingRecipient$ | async) || !!pageForm.get('destinationBankAccount').value">
          <label class="payment-method-label">Payment Method</label>
          <terra-radio-buttons [control]="pageForm.get('paymentType')" [details]="paymentMethodDetails">
          </terra-radio-buttons>
        </div>

        <ng-container *ngIf="pageForm.get('accountsSource').value === AccountsSource.External">
          <!--   Add recipient button   -->
          <div class="add-existing-recipient-container">
              <button mat-flat-button type="button" color="primary" class="add-recipient-button" (click)="addExistingRecipient()">
                <mat-icon class="notranslate">person_add</mat-icon>
                Add Recipient From List
              </button>
              <mat-error *ngIf="submitFormError && !(selectedExistingRecipient$ | async)">
                Please choose existing recipient
              </mat-error>
          </div>
        </ng-container>

        <ng-container *ngIf="pageForm.get('accountsSource').value === AccountsSource.NewExternal || !!(selectedExistingRecipient$ | async)">
          <div class="form-fields">
            <!--  Recipient First Name -->
            <div class="terra-form-field-wrapper">
              <label for="txtRecipientFirstName" class="text-input-label">Company/First Name</label>
              <mat-form-field appearance="outline">
                <input matInput id="txtRecipientFirstName" formControlName="recipientFirstName" placeholder="e.g. Dave"/>
                <mat-error *ngIf="pageForm.get('recipientFirstName').hasError('required')">
                  Required
                </mat-error>
                <mat-error *ngIf="pageForm.get('recipientFirstName').hasError('maxlength')">
                  Enter a name up to 40 characters
                </mat-error>
                <mat-error *ngIf="pageForm.get('recipientFirstName').hasError('illegalCharacter')">
                  Name shouldn't contain the following characters: {{ forbiddenNameCharacters.join(', ') }}
                </mat-error>
              </mat-form-field>
            </div>

            <!--  Recipient Last Name -->
            <div class="terra-form-field-wrapper">
              <label for="txtRecipientLastName" class="text-input-label">Last Name (optional)</label>
              <mat-form-field appearance="outline">
                <input matInput id="txtRecipientLastName" formControlName="recipientLastName" placeholder="Johnson"/>
                <mat-error *ngIf="pageForm.get('recipientLastName').hasError('maxlength')">
                  Enter a name up to 40 characters
                </mat-error>
                <mat-error *ngIf="pageForm.get('recipientLastName').hasError('illegalCharacter')">
                  Name shouldn't contain the following characters: {{ forbiddenNameCharacters.join(', ') }}
                </mat-error>
              </mat-form-field>
            </div>
          </div>

          <div class="form-fields">
            <!--  Routing Number -->
            <div class="terra-form-field-wrapper">
              <label for="routingNumber" class="text-input-label">US ABA Routing Number (RTN)</label>
              <mat-form-field appearance="outline">
                <input matInput type="tel" id="routingNumber" formControlName="routingNumber" placeholder="e.g 122105155"/>
                <mat-error *ngIf="pageForm.get('routingNumber').hasError('required')">
                  Required
                </mat-error>
                <mat-error *ngIf="pageForm.get('routingNumber').hasError('pattern')">
                  Please enter a valid 9 digits routing number
                </mat-error>
              </mat-form-field>
            </div>
            <!--  Account Number -->
            <div class="terra-form-field-wrapper">
              <label for="accountNumber" class="text-input-label">Account Number</label>
              <mat-form-field appearance="outline">
                <input matInput type="tel" id="accountNumber" formControlName="accountNumber" placeholder="e.g 123365496"/>
                <mat-error *ngIf="pageForm.get('accountNumber').hasError('required')">
                  Required
                </mat-error>
                <mat-error *ngIf="pageForm.get('accountNumber').hasError('pattern')">
                  Please enter a valid account number
                </mat-error>
              </mat-form-field>
            </div>
            <!-- Confirm Account Number -->
            <div class="terra-form-field-wrapper">
              <label for="confirmAccountNumber" class="text-input-label">Confirm Account Number</label>
              <mat-form-field appearance="outline">
                <input matInput type="tel" id="confirmAccountNumber" formControlName="confirmAccountNumber"/>
                <mat-error *ngIf="pageForm.get('confirmAccountNumber').hasError('required')">
                  Required
                </mat-error>
                <mat-error *ngIf="pageForm.get('confirmAccountNumber').hasError('pattern')">
                  Please enter a valid account number
                </mat-error>
                <mat-error *ngIf="pageForm.get('confirmAccountNumber').hasError('confirmedValidator')">
                  Please confirm your account number
                </mat-error>
              </mat-form-field>
            </div>
          </div>
          <ng-container *ngIf="pageForm.get('paymentType').value === UnitPaymentType.Wire">
            <terra-address-form [externalForm]="pageForm" [formSettings]="addressFormSettings" (formReady)="addressFormReady($event)"
            [lockUsaCountry]="true"
            class="form-fields address-form" partialFormName="address" street2Label="Second line of address"
            streetLabel="Street address">
          </terra-address-form>
          </ng-container>

          <!-- Wire memo OBI -->
          <div class="form-fields">
            <div class="terra-form-field-wrapper triple-field">
              <label for="memo" class="text-input-label">Memo / Special Instruction (optional)
                <mat-icon class="info-icon-tooltip" pTooltip="{{memoTooltipText}}" tooltipPosition="top">help_outline</mat-icon>
              </label>
              <mat-form-field appearance="outline">
                <input matInput #memo id="memo" formControlName="memo" placeholder="e.g Real Estate inc. Account Number: 123456789" maxlength="{{memoLength}}"/>
                <span matSuffix>{{ (memo.value?.length || 0) }}/{{ memoLength }}</span>
                <mat-error *ngIf="pageForm.get('memo').hasError('maxlength')">
                  Enter a description up to {{ memoLength }} characters
                </mat-error>
              </mat-form-field>
            </div>
          </div>
        </ng-container>

      </section>
      

      <section class="form-section">
        <h3 class="section-title">Payment Details</h3>
        
        <div class="form-fields">
          <!-- Amount -->
          <div class="terra-form-field-wrapper">
            <div class="label-container">
              <label for="amount" class="text-input-label">Amount</label>
              <ng-container *ngIf="selectedAccountLimits$ | async as accountLimits">
                <terra-buttons
                  type="link"
                  label="Transfer limits"
                  [outlinedIcon]="true"
                  (onClick)="openUnitAccountLimit(accountLimits)"
                ></terra-buttons>
              </ng-container>
            </div>

            <mat-form-field appearance="outline">
              <input matInput terraFormattedNumber type="tel" id="amount" formControlName="amount" placeholder="e.g. 7,500"/>
              <span matSuffix>USD</span>
              <mat-error *ngIf=" pageForm.get('amount').hasError('required')">
                Required
              </mat-error>
              <mat-error *ngIf="pageForm.get('paymentType').value === UnitPaymentType.Wire && 
              pageForm.get('amount').hasError('maxFormattedNumber') && !pageForm.get('amount').hasError('secondaryMaxFormattedNumber')">
                The requested amount (Including Wire fees) exceeds your bank account balance
              </mat-error>
              <mat-error *ngIf="pageForm.get('paymentType').value === UnitPaymentType.Ach && 
              pageForm.get('amount').hasError('maxFormattedNumber') && !pageForm.get('amount').hasError('secondaryMaxFormattedNumber')">
                The requested amount exceeds your bank account balance
              </mat-error>
              <mat-error *ngIf="pageForm.get('amount').hasError('secondaryMaxFormattedNumber') && !pageForm.get('amount').hasError('maxFormattedNumber')">
                Amount exceeds your account's {{UnitPaymentType.toString(pageForm.get('paymentType').value)}} payment limit
              </mat-error>
              <mat-error *ngIf="pageForm.get('amount').hasError('secondaryMaxFormattedNumber') && pageForm.get('amount').hasError('maxFormattedNumber')">
                The requested amount exceeds both your account balance and your {{UnitPaymentType.toString(pageForm.get('paymentType').value)}} limit
              </mat-error>
              
              <mat-error *ngIf=" pageForm.get('amount').hasError('minFormattedNumber')">
                Amount should be greater than 0
              </mat-error>
            </mat-form-field>
          </div>
          <!-- Description -->
          <div class="terra-form-field-wrapper large-field">
            <label for="description" class="text-input-label">Description (External)</label>
            <mat-form-field appearance="outline">
              <input matInput #description id="description" formControlName="description" placeholder="e.g Renovation" maxlength="{{descriptionLength}}"/>
              <span matSuffix>{{ (description.value?.length || 0) }}/{{ descriptionLength }}</span>
              <mat-error *ngIf="pageForm.get('description').hasError('required')">
                Required
              </mat-error>
              <mat-error *ngIf="pageForm.get('description').hasError('maxlength')">
                Enter a description up to {{ descriptionLength }} characters
              </mat-error>
            </mat-form-field>
          </div>
          <!-- Category -->
          <div class="terra-form-field-wrapper">
            <label for="category" class="text-input-label">Category</label>
            <mat-form-field appearance="outline">
              <mat-select id="category" placeholder="Select" formControlName="category">
                <mat-option [value]="null">Select</mat-option>
                <mat-option *ngFor="let category of UnitPaymentCategory.listAll()"
                            [value]="category">{{ category | enumString : UnitPaymentCategory }}
                </mat-option>
              </mat-select>
              <mat-error *ngIf=" pageForm.get('category').hasError('required')">
                Required
              </mat-error>
            </mat-form-field>
          </div>

          <!--Internal Notes-->
          <div class="terra-form-field-wrapper large-field">
            <label for="internalNotes" class="text-input-label">Internal Notes (Optional)</label>
            <mat-form-field appearance="outline">
              <input matInput id="internalNotes" formControlName="internalNotes"/>
              <mat-error *ngIf="pageForm.get('internalNotes').hasError('maxlength')">
                Enter Notes up to 400 characters
              </mat-error>
            </mat-form-field>
          </div>

          <!-- Reference Number -->
          <div class="terra-form-field-wrapper">
            <label for="referenceNumber" class="text-input-label">Reference Number (Optional)</label>
            <mat-form-field appearance="outline">
              <input matInput id="referenceNumber" formControlName="referenceNumber" placeholder="e.g 125AX5"/>
              <!--              <mat-error *ngIf=" pageForm.get('referenceNumber').hasError('required')">-->
              <!--                Required-->
              <!--              </mat-error>-->
            </mat-form-field>
          </div>


        </div>
      </section>
    </div>

    <mat-error class="error" *ngIf="accountLimitError$ | async">
      Your requested transfer amount exceeds the limits of your account. If you'd like to adjust these limits, please contact -
      <a mat-menu-item (click)="talkToUS()" [routerLink]="paths.SUPPORT">
        <mat-icon class="action-icon notranslate material-icons-outlined">support_agent</mat-icon>
        <span>Contact Support</span>
      </a>
    </mat-error>
    <mat-error class="error" *ngIf="generalServerError$ | async">
      An error has occurred. Please try again, and if no luck, please contact support.
    </mat-error>

    <span class="action-buttons">
      <button mat-flat-button type="submit" color="primary" class="next-button">
        Next
      </button>

      <button mat-stroked-button type="button" (click)="goBack()">
        Back
      </button>
    </span>
  </form>
</ng-template>

<terra-unit-bank-sticky-footer
  [bankingProviderString]="bankingProviderString$ | async"
  [accountProvider]="accountProvider$ | async"></terra-unit-bank-sticky-footer>
